import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment-timezone';
import { useLocation } from 'react-router-dom';
import { usePortal } from 'context';
import config from 'config/default';
import EditCTA from './Edit';
import Button from 'components/Button';
import { downloadQRPNG, downloadQRPDF, downloadFamilyReportPDF } from 'services/schedule';
import copyIcon from 'assets/copy-video-page-icon.svg';
import viewIcon from 'assets/view-video-page-icon.svg';
import './eventsTable.scss';

const timezones = {
    'America/Los_Angeles': 'Pacific Time',
    'America/Phoenix': 'Arizona Time',
    'America/Denver': 'Mountain Time',
    'America/Chicago': 'Central Time',
    'America/New_York': 'Eastern Time',
};

const SCROLL_OFFSET = 160;
const NO_EVENTS_FOUND_SCROLL_OFFSET = 122;
const SCROLL_DISABLED_TIMEOUT = 1000; // 1 second

const loadingOlderDefaultMessage = 'Loading older events...';
const getNoEventsFoundMessage = rangeStrs =>
    `No events found between ${rangeStrs.search.start} and ${rangeStrs.search.end}.`;

const pinToString = pin => (pin ? `${pin.substring(0, 3)}-${pin.substring(3, 6)}` : '');

const eventToUrl = (username, eventId, pin, isPrivate) => {
    const url = `${config.viewlogiesClientUrl}/${username}/${eventId}`;
    return !isPrivate && pin ? `${url}?pin=${pin}` : url;
};

const EventsTable = () => {
    const [showPagingOlder, setShowPagingOlder] = useState(false);
    const [isFetchingEvents, setIsFetchingEvents] = useState(false);
    const [loadingOlderMessage, setLoadingOlderMessage] = useState(loadingOlderDefaultMessage);
    const [isScrollDisabled, setIsScrollDisabled] = useState(false); // TODO
    const [scrollOffset, setScrollOffset] = useState(SCROLL_OFFSET);
    const location = useLocation();
    const prevPathnameRef = useRef(location.pathname);

    const {
        user: { username, timezone, funeralArrangers, role, partnerAccent },
        fetchEvents,
        events,
        eventsPage,
        eventsRange,
        eventsTableScroll,
    } = usePortal();

    // fetch events on initial mount
    useEffect(() => {
        fetchEvents(username);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // refresh events when navigating back to dashboard from schedule/success pages
    useEffect(() => {
        const isDashboardRoute = location.pathname === `/${username}`;
        const prevPathname = prevPathnameRef.current;
        const wasOnScheduleRoute = prevPathname.includes('/schedule');

        // Refresh events when navigating TO dashboard FROM schedule/success pages
        // Only refresh if events have been loaded (prevents refresh on initial mount)
        if (isDashboardRoute && wasOnScheduleRoute && events !== null) {
            fetchEvents(username, 0);
        }

        // Update previous pathname after checking
        prevPathnameRef.current = location.pathname;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, username]);

    useEffect(() => {
        const table = document.getElementsByTagName('table')[0];
        if (!table) {
            return;
        }
        table.scrollTo(0, SCROLL_OFFSET);
    }, [events]);

    const [copyToastMessage, setCopyToastMessage] = useState('');

    useEffect(() => {
        if (!copyToastMessage) {
            return undefined;
        }
        const timeout = setTimeout(() => setCopyToastMessage(''), 2000);
        return () => clearTimeout(timeout);
    }, [copyToastMessage]);

    if (!events) {
        return null;
    }

    const rangeStrs = {
        all: {
            start: moment(eventsRange.all.startTime).tz(timezone).format('M/D/YY'), // these dates are displayed in utc because that's how they're stored in db
            end: moment(eventsRange.all.endTime).tz(timezone).format('M/D/YY'),
        },
        search: {
            start: moment(eventsRange.search.startTime).tz(timezone).format('M/D/YY'),
            end: moment(eventsRange.search.endTime).tz(timezone).format('M/D/YY'),
        },
        tz: timezones[timezone],
    };

    const eventsRangeMessage = `Showing events for ${rangeStrs.all.start} to ${rangeStrs.all.end}.${
        rangeStrs.tz ? ` Event times are displayed in your device's timezone.` : ''
    }`;

    const getCircleColor = stream => {
        let circleColor = 'green';
        if (stream.ready === false && stream.isMobile === false) {
            circleColor = 'red';
        }
        return circleColor;
    };

    const enableEditAbility = event => {
        //if user role is admin or superadmin
        if (role === 'admin' || role === 'superadmin') {
            return true;
        }

        const lowercaseFuneralArrangers = funeralArrangers.map(email => email.toLowerCase());
        const arrangerEmail = event.arrangerEmail.toLowerCase();
        if (lowercaseFuneralArrangers.includes(arrangerEmail)) {
            return true;
        }
        return false;
    };

    const fetchPastEvents = table => {
        setIsFetchingEvents(true);
        fetchEvents(username, eventsPage - 1).then(count => {
            if (count === 0) {
                if (events.length === 0) {
                    setLoadingOlderMessage(loadingOlderDefaultMessage);
                    table.scrollTo(0, SCROLL_OFFSET);
                    setScrollOffset(SCROLL_OFFSET);
                    setShowPagingOlder(false);
                } else {
                    setLoadingOlderMessage(getNoEventsFoundMessage(rangeStrs));
                    table.scrollTo(0, NO_EVENTS_FOUND_SCROLL_OFFSET);
                    setScrollOffset(NO_EVENTS_FOUND_SCROLL_OFFSET);
                    setShowPagingOlder(true);
                    setTimeout(() => {
                        table.scrollTo(0, SCROLL_OFFSET);
                        setScrollOffset(SCROLL_OFFSET);
                        setShowPagingOlder(false);
                    }, 5000);
                }
            } else {
                setShowPagingOlder(false);
                table.scrollTo(0, SCROLL_OFFSET);
                setScrollOffset(SCROLL_OFFSET);
            }
            setIsFetchingEvents(false);
            setIsScrollDisabled(true);
            setTimeout(() => setIsScrollDisabled(false), SCROLL_DISABLED_TIMEOUT);
        });
    };

    const handleScroll = ({ target: table }) => {
        if (eventsTableScroll === 'hidden') {
            return;
        }
        if (isScrollDisabled) {
            // keep scroll at scroll x position set in state
            table.scrollTo(0, scrollOffset);
        } else if (table.scrollTop === 0) {
            setShowPagingOlder(true);
            if (!isFetchingEvents) {
                setIsFetchingEvents(true);
                fetchPastEvents(table);
            }
        } else if (showPagingOlder && table.scrollTop > 120) {
            setShowPagingOlder(false);
        }
    };

    const handleCopyLink = async link => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(link);
                setCopyToastMessage('Copied video page URL');
                return;
            }
            const textarea = document.createElement('textarea');
            textarea.value = link;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            const didCopy = document.execCommand('copy');
            document.body.removeChild(textarea);
            if (didCopy) {
                setCopyToastMessage('Copied video page URL');
            }
        } catch (error) {
            console.error('Failed to copy link:', error);
        }
    };

    return (
        <section className="events-table-container">
            <h2 style={{ color: partnerAccent ? partnerAccent : undefined }}>{'Event Manager'}</h2>
            {copyToastMessage && <div className="events-toast">{copyToastMessage}</div>}
            <table className="events-table" onScroll={handleScroll}>
                <thead>
                    <tr>
                        <th>Ready</th>
                        <th>Name</th>
                        <th>Camera</th>
                        <th>Time</th>
                        <th>Edit</th>
                        <th>QR Sign</th>
                        <th>QR PNG</th>
                        <th>Slideshow</th>
                        <th>PIN</th>
                        <th>Family Report</th>
                        <th>Copy Video Page URL</th>
                        <th>View Video Page</th>
                    </tr>
                </thead>
                <tbody>
                    <tr className="tr-spacer">
                        {/* hacky little placeholder for keeping header row and body rows aligned, while allowing space to scroll up */}
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                        <td />
                    </tr>
                    <tr>
                        <td colSpan={12}>
                            <p className={`loading-more loading-more-top${showPagingOlder ? '' : ' hidden'}`}>
                                {loadingOlderMessage}
                            </p>
                        </td>
                    </tr>
                    {events && events.length === 0 && <p>{getNoEventsFoundMessage(rangeStrs)}</p>}
                    {events.map((event, i) => {
                        const eventUrl = eventToUrl(username, event.id, event.pin, event.isPrivate);
                        const eventEndMoment = event.streams.length
                            ? event.streams.reduce((latest, stream) => {
                                  const end = moment(stream.endTime);
                                  return !latest || end.isAfter(latest) ? end : latest;
                              }, null)
                            : null;
                        const isReportReady = eventEndMoment ? moment().isAfter(eventEndMoment) : false;
                        const reportTooltip = eventEndMoment
                            ? `You can download the report after the event ${eventEndMoment.format('MMM D, h:mm A')}.`
                            : 'You can download the report after the event ends.';
                        let uploadPageLink = null;
                        if (event.slideshowVimeoToken) {
                            uploadPageLink = (
                                <a
                                    className="event-item-value"
                                    href={`${config.viewlogiesClientUrl}/upload/${username}/${event.id}?token=${event.slideshowVimeoToken}`}
                                    target="_blank"
                                    rel="noopen noreferrer"
                                    onClick={e => e.stopPropagation()}
                                >
                                    {event.isSlideshowUploadComplete ? 'Replace' : 'Upload'}
                                </a>
                            );
                        }
                        const qrSignLink = (
                            <Button
                                text="Print"
                                onClick={() => downloadQRPDF(username, event.id, event.firstName)}
                                className="link-button"
                            />
                        );
                        const qrPNGLink = (
                            <Button
                                text="Download"
                                onClick={() => downloadQRPNG(username, event.id, event.firstName)}
                                className="link-button"
                            />
                        );
                        const familyReportButton = (
                            <span className="events-tooltip-target">
                                <Button
                                    text="Download"
                                    onClick={() =>
                                        downloadFamilyReportPDF(username, event.pin, event.lastName || event.firstName)
                                    }
                                    className={`link-button${isReportReady ? '' : ' disabled'}`}
                                    disabled={!isReportReady}
                                />
                            </span>
                        );
                        const familyReportLink = event.pin ? (
                            isReportReady ? (
                                familyReportButton
                            ) : (
                                <span className="events-tooltip" data-tooltip={reportTooltip}>
                                    {familyReportButton}
                                </span>
                            )
                        ) : null;
                        const copyLinkButton = (
                            <button
                                type="button"
                                className="event-icon-button"
                                onClick={e => {
                                    e.stopPropagation();
                                    handleCopyLink(eventUrl);
                                }}
                                aria-label="Copy video page URL"
                            >
                                <img className="event-icon" src={copyIcon} alt="Copy video page URL" />
                            </button>
                        );
                        const viewLinkButton = (
                            <a
                                className="event-icon-button"
                                href={eventUrl}
                                target="_blank"
                                rel="noopen noreferrer"
                                onClick={e => e.stopPropagation()}
                                aria-label="View video page"
                            >
                                <img className="event-icon" src={viewIcon} alt="View video page" />
                            </a>
                        );
                        return (
                            <tr key={i}>
                                <td>
                                    {event.streams.map((stream, j) => (
                                        <div
                                            key={j}
                                            className={`ready-circle ready-circle-${getCircleColor(stream)}`}
                                        />
                                    ))}
                                </td>
                                <td>{`${event.firstName} ${event.lastName}`}</td>
                                <td>
                                    {event.streams.map((stream, j) => (
                                        <div key={j}>{stream.chapel}</div>
                                    ))}
                                </td>
                                {event.streams.length ? (
                                    <td>
                                        {event.streams.map((stream, j) => (
                                            <div key={j}>
                                                {moment(stream.startTime).format('M/D h:mmA')}-
                                                {moment(stream.endTime).format('h:mmA')}
                                            </div>
                                        ))}
                                    </td>
                                ) : (
                                    <td>{'No streams scheduled.'}</td>
                                )}
                                <td>{enableEditAbility(event) && <EditCTA id={event.id} />}</td>
                                <td>{qrSignLink}</td>
                                <td>{qrPNGLink}</td>
                                <td>{uploadPageLink}</td>
                                <td>
                                    {pinToString(event.pin) ||
                                        event.streams.map((stream, j) => <div key={j}>{pinToString(stream.pin)}</div>)}
                                </td>
                                <td>{familyReportLink}</td>
                                <td className="icon-cell">{copyLinkButton}</td>
                                <td className="icon-cell">{viewLinkButton}</td>
                            </tr>
                        );
                    })}
                    <tr className="tr-spacer tr-spacer-bottom" />
                </tbody>
            </table>
            <div className="events-dates">
                <p>{eventsRangeMessage}</p>
            </div>
        </section>
    );
};

export default EventsTable;
